/** @format */
import {
    Choice,
    ChoiceLabelSize,
    ChoiceVariant,
} from '@mpb-com/feather/components/atoms/choice';
import { Input } from '@mpb-com/feather/components/molecules/input';
import { InputProps } from '@mpb-com/feather/components/molecules/input/types';
import { useTranslation } from 'next-i18next';
import React, {
    ChangeEvent,
    FunctionComponent,
    KeyboardEvent,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import styled from 'styled-components';

import {
    centToDecimal,
    decimalToCent,
    getCurrencySymbol,
} from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';

import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';
import { FilterOption } from '@toucan/client/features/search/components/model-search-results/types';

import { SearchFilterMenuOptionList } from '../option-list';
import {
    CustomRangeField,
    DEFAULT_TEST_ID,
    MAX_CUSTOM_RANGE,
    PriceFilterOption,
} from './constants';
import { styles } from './price-options.styles';
import { SearchFilterMenuPriceOptionsProps } from './types';

const StyledSearchFilterMenuPriceOptions = styled.div`
    && {
        ${styles}
    }
`;

export const SearchFilterMenuPriceOptions: FunctionComponent<
    SearchFilterMenuPriceOptionsProps
> = ({
    onClear,
    filter,
    selectedItems,
    setSelectedItems,
    isLoading = false,
    autoFocusPriceGroup,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const currencySymbol = getCurrencySymbol(
        activeMarket.iso,
        activeMarket.currency
    );

    const { variants } = useSearchParams();

    const productPriceVariant = variants.productPrice as PriceFilterOption;

    const [variant, setVariant] = useState<PriceFilterOption>(
        productPriceVariant ?? PriceFilterOption.GROUP
    );
    const [customRange, setCustomRange] = useState(getInitialCustomRange);

    const selectedItemsByPriceGroup = useMemo(() => {
        if (variant === PriceFilterOption.RANGE) {
            return [];
        }

        return selectedItems.filter(
            (item: FilterOption) => item.variant === PriceFilterOption.GROUP
        );
    }, [selectedItems, variant]);

    const optionListValues = useMemo(() => {
        return filter.values.filter(
            (value: FilterOption) => value.variant === PriceFilterOption.GROUP
        );
    }, [filter.values]);

    function clearCustomRangeInput() {
        setCustomRange({ from: '', to: '' });
    }

    function serializeCustomRangeValue(
        value: string | number,
        convertFunction: (v: number) => number
    ) {
        if (!value || isNaN(Number(value))) {
            return '';
        }

        return Math.min(convertFunction(Number(value)), MAX_CUSTOM_RANGE);
    }

    function getInitialCustomRange() {
        if (variant === PriceFilterOption.GROUP) {
            return { from: '', to: '' };
        }

        const value = selectedItems?.[0]?.value ?? '';
        const [from, to] = `${value}`.split('-');

        return {
            from: serializeCustomRangeValue(from, centToDecimal),
            to: serializeCustomRangeValue(to, centToDecimal),
        };
    }

    function getCustomRangeItem(from: string | number, to: string | number) {
        const serializedFrom = serializeCustomRangeValue(from, decimalToCent);
        const serializedTo = serializeCustomRangeValue(to, decimalToCent);

        if (serializedFrom || serializedTo) {
            return { value: `${serializedFrom || 0}-${serializedTo}` };
        }

        return null;
    }

    function onCustomRangeInputFocus() {
        setVariant(PriceFilterOption.RANGE);
        if (!(customRange.from || customRange.to)) {
            onClear?.();
        }
    }

    function onCustomRangeInputKeyPress(
        event: KeyboardEvent<HTMLInputElement>
    ) {
        // HTML number inputs still allow these keys although here we don't want to
        const disallowedKeys = ['e', '-', '+'];

        if (disallowedKeys.includes(event.key)) {
            event.preventDefault();
        }
    }

    function onCustomRangeChange(value: string, field: CustomRangeField) {
        const from = field === CustomRangeField.FROM ? value : customRange.from;
        const to = field === CustomRangeField.TO ? value : customRange.to;

        setCustomRange({ from, to });

        const customRangeItem = getCustomRangeItem(from, to);

        if (!customRangeItem) {
            onClear?.();
            return;
        }

        setSelectedItems([
            {
                label: '',
                value: customRangeItem.value,
                quantity: 0,
                variant: PriceFilterOption.RANGE,
            },
        ]);
    }

    function onGroupSelect(options: FilterOption[]) {
        if (variant === PriceFilterOption.RANGE) {
            clearCustomRangeInput();
            setVariant(PriceFilterOption.GROUP);
        }

        const optionsWithVariant = options
            .filter((option) => option.variant === PriceFilterOption.GROUP)
            .map((option) => ({
                ...option,
                variant: PriceFilterOption.GROUP,
            }));

        setSelectedItems(optionsWithVariant);
    }

    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    function onVariantChange(e: ChangeEvent<HTMLInputElement>) {
        const option = e.target.value as PriceFilterOption;

        if (option === PriceFilterOption.GROUP) {
            clearCustomRangeInput();
        }
        setVariant(option);

        if (productPriceVariant !== option) {
            setSelectedItems([]);
            onClear?.();
        }
    }

    useEffect(() => {
        if (!selectedItems?.length) {
            clearCustomRangeInput();
        }
    }, [selectedItems]);

    const commonInputProps: Partial<InputProps> = {
        className: 'input',
        type: 'number',
        prefixIcon: currencySymbol,
        onFocus: onCustomRangeInputFocus,
        min: 0,
        onKeyPress: onCustomRangeInputKeyPress,
    };

    return (
        <StyledSearchFilterMenuPriceOptions data-testid={dataTestId}>
            <Choice
                className="option"
                variant={ChoiceVariant.RADIO}
                name="price-options-variant"
                label={t('Filter by price group')}
                value={PriceFilterOption.GROUP}
                checked={variant === PriceFilterOption.GROUP}
                onChange={onVariantChange}
                labelSize={ChoiceLabelSize.SMALL}
            />
            <SearchFilterMenuOptionList
                filter={{
                    ...filter,
                    values: optionListValues,
                }}
                selectedItems={selectedItemsByPriceGroup}
                setSelectedItems={onGroupSelect}
                isLoading={isLoading}
                displayOptionQuantity={false}
                autoFocus={
                    autoFocusPriceGroup ?? variant === PriceFilterOption.GROUP
                }
            />
            <Choice
                className="option"
                variant={ChoiceVariant.RADIO}
                name="price-options-variant"
                label={t('Customise range')}
                value={PriceFilterOption.RANGE}
                checked={variant === PriceFilterOption.RANGE}
                onChange={onVariantChange}
                labelSize={ChoiceLabelSize.SMALL}
            />
            <div className="custom-range-inputs">
                <Input
                    data-testid={`${dataTestId}__from-input`}
                    label={t('from')}
                    value={customRange.from}
                    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
                    onChange={(e) =>
                        onCustomRangeChange(
                            e.currentTarget.value,
                            CustomRangeField.FROM
                        )
                    }
                    {...commonInputProps}
                />
                <Input
                    data-testid={`${dataTestId}__to-input`}
                    label={t('to')}
                    value={customRange.to}
                    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
                    onChange={(e) =>
                        onCustomRangeChange(
                            e.currentTarget.value,
                            CustomRangeField.TO
                        )
                    }
                    {...commonInputProps}
                />
            </div>
        </StyledSearchFilterMenuPriceOptions>
    );
};
